import { getMappedExperienceImage } from '../data/experienceImageMap';

const LEGACY_STORAGE_BASE = 'https://platform.glooci.it/storage';
const STORAGE_MEDIA_PROXY_PATH = '/gcs-object';
const FIRESTORE_ROOT = String(import.meta.env.VITE_FIRESTORE_ROOT || 'glooci_genia')
  .trim()
  .replace(/^\/+|\/+$/g, '');

function buildRootVariants(root: string): string[] {
  const candidates = [root, root.replace(/_/g, '-'), root.replace(/-/g, '_')]
    .map((item) => item.trim())
    .filter(Boolean);
  return Array.from(new Set(candidates));
}

const FIRESTORE_ROOT_VARIANTS = buildRootVariants(FIRESTORE_ROOT);
const FIREBASE_LEGACY_PREFIXES = FIRESTORE_ROOT_VARIANTS.map((root) => `${root}/legacy`);
const LEGACY_TOP_LEVEL_FOLDERS = new Set(['avatar', 'experiences', 'interactions', 'users', 'organizations', 'media']);
const EXPERIENCE_COVER_OVERRIDES_KEY = 'glooci.experienceCoverOverrides.v1';

export type LegacyStorageFolder = 'avatar' | 'experiences' | 'interactions' | 'users' | 'organizations';

function normalizeRelativeObjectPath(path: string): string {
  return String(path || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/[?#].*$/, '');
}

/**
 * Resolves a legacy image path/filename to a full absolute URL.
 * The legacy server stores images in /storage/{folder}/{filename}.
 * The field can contain:
 *   - A full URL (https://...) → used as-is
 *   - A relative path (storage/avatar/img.jpg) → prepend base
 *   - Just a filename (img_xxx.jpg) → prepend base + folder
 */
export function resolveLegacyImageUrl(
  path: string | null | undefined,
  folder: LegacyStorageFolder = 'avatar',
): string | null {
  if (!path || path.trim() === '') return null;

  const trimmed = path.trim();

  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return toProxyUrlIfFirebaseDownload(trimmed);
  }

  const normalizedRelativePath = normalizeRelativeObjectPath(trimmed);

  // Relative path already starting with storage/
  if (normalizedRelativePath.startsWith('storage/')) {
    return `https://platform.glooci.it/${normalizedRelativePath}`;
  }

  // Just a filename — resolve with folder
  return `${LEGACY_STORAGE_BASE}/${folder}/${normalizedRelativePath || trimmed}`;
}

function toFirebaseStorageMediaUrl(objectPath: string, bucket?: string): string {
  const params = new URLSearchParams();
  params.set('object', objectPath);
  if (bucket && bucket.trim() !== '') {
    params.set('bucket', bucket.trim());
  }
  return `${STORAGE_MEDIA_PROXY_PATH}?${params.toString()}`;
}

function parseFirebaseStorageDownloadUrl(rawUrl: string): { objectPath: string; bucket: string } | null {
  try {
    const url = new URL(rawUrl);
    const host = String(url.hostname || '').toLowerCase();
    if (host !== 'firebasestorage.googleapis.com' && !host.endsWith('.firebasestorage.googleapis.com')) {
      return null;
    }
    const match = url.pathname.match(/\/v0\/b\/([^/]+)\/o\/(.+)$/);
    if (!match) return null;

    const bucket = decodeURIComponent(String(match[1] || '')).trim();
    const objectPath = decodeURIComponent(String(match[2] || ''))
      .trim()
      .replace(/^\/+/, '');

    if (!bucket || !objectPath) return null;
    return { objectPath, bucket };
  } catch {
    return null;
  }
}

function toProxyUrlIfFirebaseDownload(rawUrl: string): string {
  const parsed = parseFirebaseStorageDownloadUrl(rawUrl);
  if (!parsed) return rawUrl;
  return toFirebaseStorageMediaUrl(parsed.objectPath, parsed.bucket);
}

function isAbsoluteHttpUrl(path: string): boolean {
  return path.startsWith('http://') || path.startsWith('https://');
}

function uniqueNonEmpty(items: Array<string | null | undefined>): string[] {
  const cleaned = items
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  return Array.from(new Set(cleaned));
}

type ExperienceCoverOverrides = Record<string, string>;

function readExperienceCoverOverrides(): ExperienceCoverOverrides {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(EXPERIENCE_COVER_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.entries(parsed as Record<string, unknown>).reduce<ExperienceCoverOverrides>((acc, [key, value]) => {
      const id = String(key || '').trim();
      const path = normalizeStoredPath(String(value || ''));
      if (!id || !path || isPlaceholderImage(path)) return acc;
      acc[id] = path;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function writeExperienceCoverOverrides(value: ExperienceCoverOverrides): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(EXPERIENCE_COVER_OVERRIDES_KEY, JSON.stringify(value));
  } catch {
    // ignore quota/storage errors
  }
}

export function getExperienceCoverOverride(experienceId: number | null | undefined): string | null {
  const id = Math.trunc(Number(experienceId || 0));
  if (!Number.isFinite(id) || id <= 0) return null;
  const map = readExperienceCoverOverrides();
  const value = normalizeStoredPath(map[String(id)] || '');
  return value && !isPlaceholderImage(value) ? value : null;
}

export function setExperienceCoverOverride(
  experienceId: number | null | undefined,
  path: string | null | undefined,
): void {
  const id = Math.trunc(Number(experienceId || 0));
  if (!Number.isFinite(id) || id <= 0) return;
  const map = readExperienceCoverOverrides();
  const normalized = normalizeStoredPath(path || '');
  const key = String(id);
  if (!normalized || isPlaceholderImage(normalized)) {
    delete map[key];
    writeExperienceCoverOverrides(map);
    return;
  }
  map[key] = normalized;
  writeExperienceCoverOverrides(map);
}

export function applyExperienceCoverOverrides<T extends {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
}>(items: T[]): T[] {
  return (items || []).map((item) => {
    const override = getExperienceCoverOverride(item?.id);
    if (!override) return item;
    return {
      ...item,
      image: override,
      logo: override,
    };
  });
}

function buildFirebaseObjectPathCandidates(
  path: string | null | undefined,
  folder: LegacyStorageFolder = 'experiences',
): string[] {
  if (!path || path.trim() === '') return [];
  const normalized = normalizeRelativeObjectPath(path);
  if (!normalized) return [];
  const candidates: string[] = [];
  const add = (value: string) => {
    const cleaned = String(value || '').trim().replace(/^\/+/, '');
    if (!cleaned || candidates.includes(cleaned)) return;
    candidates.push(cleaned);
  };

  const knownLegacyPrefix = FIREBASE_LEGACY_PREFIXES.find((prefix) =>
    normalized === prefix || normalized.startsWith(`${prefix}/`));
  if (knownLegacyPrefix) {
    const suffix = normalized.slice(knownLegacyPrefix.length);
    FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/legacy${suffix}`));
    add(normalized);
    return candidates;
  }

  const knownRootPrefix = FIRESTORE_ROOT_VARIANTS.find((root) =>
    normalized === root || normalized.startsWith(`${root}/`));
  if (knownRootPrefix) {
    const suffix = normalized.slice(knownRootPrefix.length).replace(/^\/+/, '');
    FIRESTORE_ROOT_VARIANTS.forEach((root) => {
      if (suffix) add(`${root}/${suffix}`);
      else add(root);
      const topLevelFolder = suffix.split('/')[0] || '';
      if (suffix && LEGACY_TOP_LEVEL_FOLDERS.has(topLevelFolder)) {
        add(`${root}/legacy/${suffix}`);
      }
    });
    add(normalized);
    return candidates;
  }

  if (normalized.startsWith('storage/')) {
    const relativeStoragePath = normalized.slice('storage/'.length);
    FIREBASE_LEGACY_PREFIXES.forEach((prefix) => add(`${prefix}/${relativeStoragePath}`));
    FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/${relativeStoragePath}`));
    return candidates;
  }

  if (normalized.startsWith('legacy/')) {
    FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/${normalized}`));
    return candidates;
  }

  if (normalized.startsWith(`${folder}/`)) {
    FIREBASE_LEGACY_PREFIXES.forEach((prefix) => add(`${prefix}/${normalized}`));
    FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/${normalized}`));
    add(normalized);
    return candidates;
  }

  if (normalized.includes('/')) {
    add(normalized);
    FIREBASE_LEGACY_PREFIXES.forEach((prefix) => add(`${prefix}/${normalized}`));
    FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/${normalized}`));
    return candidates;
  }

  FIREBASE_LEGACY_PREFIXES.forEach((prefix) => add(`${prefix}/${folder}/${normalized}`));
  FIRESTORE_ROOT_VARIANTS.forEach((root) => add(`${root}/${folder}/${normalized}`));
  add(`${folder}/${normalized}`);
  return candidates;
}

function resolveFirebaseLegacyImageUrls(
  path: string | null | undefined,
  folder: LegacyStorageFolder = 'experiences',
): string[] {
  if (!path || path.trim() === '') return [];
  const trimmed = path.trim();
  if (isAbsoluteHttpUrl(trimmed)) return [toProxyUrlIfFirebaseDownload(trimmed)];
  return buildFirebaseObjectPathCandidates(trimmed, folder).map((objectPath) => toFirebaseStorageMediaUrl(objectPath));
}

function resolveFirebaseLegacyImageUrl(
  path: string | null | undefined,
  folder: LegacyStorageFolder = 'experiences',
): string | null {
  return resolveFirebaseLegacyImageUrls(path, folder)[0] || null;
}

function isPlaceholderImage(path: string | null | undefined): boolean {
  if (!path) return true;
  const normalized = path.trim().toLowerCase();
  if (!normalized) return true;
  if (normalized === 'default.jpg' || normalized === 'default.png' || normalized === 'default.webp') return true;
  if (normalized.endsWith('/default.jpg') || normalized.endsWith('/default.png') || normalized.endsWith('/default.webp')) return true;
  if (normalized.includes('nessuna-copertina') || normalized.includes('no-image') || normalized.includes('placeholder')) return true;
  return false;
}

export function isPlaceholderAsset(path: string | null | undefined): boolean {
  return isPlaceholderImage(path);
}

function normalizeStoredPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const normalized = path.trim();
  if (!normalized) return null;
  return normalized;
}

export function getExperienceCoverSource(exp: {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
}): string | null {
  const override = getExperienceCoverOverride(exp.id);
  if (override && !isPlaceholderImage(override)) return override;

  const image = normalizeStoredPath(exp.image);
  if (image && !isPlaceholderImage(image)) return image;

  const logo = normalizeStoredPath(exp.logo);
  if (logo && !isPlaceholderImage(logo)) return logo;

  const mappedFilename = getMappedExperienceImage(exp.id);
  if (mappedFilename) return mappedFilename;

  return null;
}

/**
 * Best-effort URL for an experience cover image.
 * Checks mapped legacy filename > image > logo in priority order.
 */
export function resolveExperienceCoverUrl(exp: {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
  avatar?: string | null;
}): string | null {
  return resolveExperienceCoverCandidates(exp)[0] || null;
}

export function resolveExperienceCoverCandidates(exp: {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
  avatar?: string | null;
}): string[] {
  const source = getExperienceCoverSource(exp);
  if (!source) return [];

  const isMappedFilename = source.toLowerCase().startsWith('img-');
  if (isMappedFilename) {
    return uniqueNonEmpty([
      ...resolveFirebaseLegacyImageUrls(source, 'experiences'),
      resolveLegacyImageUrl(source, 'experiences'),
    ]);
  }

  const normalized = normalizeStoredPath(source);
  if (!normalized) return [];

  const candidates: string[] = [];
  const hasSlash = normalized.includes('/');
  const experienceId = Math.trunc(Number(exp.id || 0));
  const canUseIdScopedPath = !hasSlash && Number.isFinite(experienceId) && experienceId > 0;

  // New platform uploads store cover in:
  //   {root}/legacy/experiences/{filename}
  // and resolveFirebaseLegacyImageUrls(normalized) already prioritizes this path.
  candidates.push(...resolveFirebaseLegacyImageUrls(normalized, 'experiences'));
  candidates.push(resolveLegacyImageUrl(normalized, 'experiences') || '');

  if (canUseIdScopedPath) {
    // Fallback only for very old id-scoped legacy uploads.
    candidates.push(...resolveFirebaseLegacyImageUrls(`experiences/${experienceId}/${normalized}`, 'experiences'));
    candidates.push(...resolveFirebaseLegacyImageUrls(`legacy/experiences/${experienceId}/${normalized}`, 'experiences'));
    candidates.push(
      resolveLegacyImageUrl(`storage/experiences/${experienceId}/${normalized}`, 'experiences') || '',
      resolveLegacyImageUrl(`experiences/${experienceId}/${normalized}`, 'experiences') || '',
    );
  }

  return uniqueNonEmpty(candidates);
}

/**
 * Returns true when an experience has no resolvable cover and should display "Nessuna copertina".
 */
export function hasExperienceCover(exp: {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
  avatar?: string | null;
}): boolean {
  return Boolean(resolveExperienceCoverUrl(exp));
}

/**
 * Backward-compatible fallback that can also show avatar when no cover is available.
 */
export function resolveExperiencePreviewUrl(exp: {
  id?: number | null;
  image?: string | null;
  logo?: string | null;
  avatar?: string | null;
}): string | null {
  return (
    resolveExperienceCoverUrl(exp) ||
    (!isPlaceholderImage(exp.avatar) ? resolveAvatarUrl(exp.avatar) : null) ||
    null
  );
}

/**
 * Best-effort URL for a Genius Loci / chatbot avatar.
 */
export function resolveAvatarUrl(path: string | null | undefined): string | null {
  if (isPlaceholderImage(path)) return null;
  const normalized = path?.trim() || '';
  if (!normalized) return null;

  if (isAbsoluteHttpUrl(normalized)) return normalized;

  const shouldResolveAsCover =
    normalized.toLowerCase().startsWith('img-') ||
    /^e[0-9a-z]+_[a-z0-9]+\.(jpg|jpeg|png|webp)$/i.test(normalized) ||
    normalized.startsWith('experiences/') ||
    normalized.startsWith('/experiences/') ||
    normalized.startsWith('storage/experiences/') ||
    normalized.startsWith('/storage/experiences/');

  if (shouldResolveAsCover) {
    return (
      resolveFirebaseLegacyImageUrl(normalized, 'experiences') ||
      resolveLegacyImageUrl(normalized, 'experiences')
    );
  }

  return (
    resolveFirebaseLegacyImageUrl(normalized, 'avatar') ||
    resolveLegacyImageUrl(normalized, 'avatar') ||
    resolveFirebaseLegacyImageUrl(normalized, 'experiences') ||
    resolveLegacyImageUrl(normalized, 'experiences')
  );
}

export function resolveOrganizationLogoUrl(path: string | null | undefined): string | null {
  return resolveOrganizationLogoCandidates(path)[0] || null;
}

export function resolveOrganizationLogoCandidates(path: string | null | undefined): string[] {
  if (isPlaceholderImage(path)) return [];
  return uniqueNonEmpty([
    ...resolveFirebaseLegacyImageUrls(path, 'organizations'),
    resolveLegacyImageUrl(path, 'organizations'),
  ]);
}

/**
 * Returns avatar URL with fallback to the experience cover.
 * When `isFallback: true`, apply `object-position: top center` to crop the square from the top
 * (experience covers are usually vertical rectangles).
 */
export function resolveAvatarWithFallback(exp: {
  id?: number | null;
  avatar?: string | null;
  image?: string | null;
  logo?: string | null;
}): { url: string | null; isFallback: boolean } {
  const avatarUrl = resolveAvatarUrl(exp.avatar);
  if (avatarUrl) return { url: avatarUrl, isFallback: false };

  const coverUrl = resolveExperienceCoverUrl(exp);
  if (coverUrl) return { url: coverUrl, isFallback: true };

  return { url: null, isFallback: false };
}
